import React from 'react';
import { NodeHeader } from './components/NodeHeader';
import { NodeWrapper } from './components/NodeWrapper';
import { Body5, Container } from '@ui';
import PlanStepsSourceHandle from './components/handles/PlanStepsSourceHandle';
import PlanStepsTargetHandle from './components/handles/PlanStepsTargetHandle';
import styled from 'styled-components';
import { FlatButton, TransparentFlatButton } from '@ui/buttons';
import { NodeDebugger } from './components/NodeDebugger';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';
import {
  PlanStepsYesConditionalSourceHandle,
  PlanStepsNoConditionalSourceHandle,
} from './components/handles/PlanStepsConditionalSourceHandle';
import { AgentFlowParameterWrapper } from '../styles';

import { StringListParameter } from '../components/AgentFlow/StringListParameter';
import { FunctionListParameter } from '../components/AgentFlow/FunctionListParameter';
import { ExtendedListParameter } from '../components/AgentFlow/ExtendedListParameter';
import { MixedListParameter } from '../components/AgentFlow/MixedListParameter';
import { StringListOrValueParameter } from '../components/AgentFlow/StringListOrValueParameter';
import { DocSelectionParameter } from '../components/AgentFlow/DocSelectionParameter';
import { InputParameter } from '../components/AgentFlow/InputParameter';
import { TextAreaParameter } from '../components/AgentFlow/TextAreaParameter';
import { DropdownParameter } from '../components/AgentFlow/DropdownParameter';
import { BoolParameter } from '../components/AgentFlow/BoolParameter';
import { CsvDocSelectionParameter } from '../components/AgentFlow/CsvDocSelectionParameter';
import { FixedOptionListParameter } from '../components/AgentFlow/FixedOptionListParameter';
import { AdvancedSettingsParameter } from '../components/AgentFlow/AdvancedSettingsParameter';
import { DATA_ANALYST_SOURCES } from '@constants/dataAnalystAgentSources';
import { StepJsonDisplay } from '../components/AgentFlow/StepJsonDisplay';
import { NodeValidationDisplay, NodeValidationBadge } from '../components/ValidationDisplay';

const SettingStyling = styled.div`
  width: 100%;

  input,
  textarea {
    font-size: 14px;
    border: none;
    color: ${({ theme }) => theme.themeColors.text};
    background-color: ${({ theme }) => theme.themeColors.lowContrastBorder};
    border-radius: 4px;
    width: 100%;
    padding: 8px;
  }

  textarea {
    min-height: 68px;
    width: 100%;
  }

  ul {
    margin: 4px 0 0 0;
    padding: 0 36px 0 24px;

    li {
      &:first-child {
        padding-top: 0;
      }

      padding: 8px 0 0 0;
    }
  }

  select {
    //border: 1px solid ${({ theme }) => theme.themeColors.border};
    border: none;
    color: ${({ theme }) => theme.themeColors.text};
    background-color: ${({ theme }) => theme.themeColors.lowContrastBorder};
    border-radius: 4px;
    width: 100%;
    padding: 8px;
    -webkit-appearance: auto !important;
  }
`;
const ContentWrapper = styled.div`
  padding: 4px 8px;
  max-width: 496px;
  min-width: 496px;
`;

const AddSelectedSettingButton = styled.div`
  padding-left: 8px;

  button {
    padding: 6.5px 12px;
  }
`;
const ConditionalNextStepsButtonWrapper = styled.div`
  padding-top: 8px;
  padding-bottom: 4px;
  text-align: right;

  button {
    padding: 6.5px 12px;
  }
`;

/*
// const NOT_ADDED_SETTINGS = {
//   defaultConditionalNextStepIds: 'default_conditional_next_step_ids',

//   nextStepIds: 'next_step_ids',
//   conditionalNextStepIds: 'conditional_next_step_ids',
// };
*/

class PlanStepSettings {
  constructor({
    keyName,
    label,
    inputType,
    dropdownOptions,
    fixedOptions,
    prioritySort,
    excludedStepTypes,
    supportedStepTypes,
  }) {
    this.keyName = keyName;
    this.label = label;
    this.inputType = inputType;
    this.dropdownOptions = dropdownOptions;
    this.fixedOptions = fixedOptions;
    this.prioritySort = prioritySort;
    this.excludedStepTypes = excludedStepTypes;
    this.supportedStepTypes = supportedStepTypes;

    if (inputType === PLAN_STEP_TYPES.bool) {
      this.defaultValue = true;
    }
    if (
      [
        PLAN_STEP_TYPES.list,
        PLAN_STEP_TYPES.extendedList,
        PLAN_STEP_TYPES.mixedList,
        PLAN_STEP_TYPES.functionList,
        PLAN_STEP_TYPES.fixedOptionList,
        PLAN_STEP_TYPES.csvDocSelection,
      ].includes(inputType)
    ) {
      this.defaultValue = [];
    }
  }
}

const PLAN_STEP_TYPES = {
  text: 'text',
  textarea: 'textarea',
  bool: 'bool',
  dropdown: 'dropdown',
  list: 'list',
  extendedList: 'extendedList',
  mixedList: 'mixedList',
  listOrValue: 'listOrValue',
  docSelection: 'docSelection',
  functionList: 'functionList',
  advSettings: 'advSettings',
  csvDocSelection: 'csvDocSelection',
  fixedOptionList: 'fixedOptionList',
};

const STEP_TYPE_OPTIONS = ['query', 'code', 'excel_sensitivity_analysis', 'data_analyst_agent'];
const STEP_TYPE_PARAMETER_LOOKUP = {
  code: ['code'],
  data_analyst_agent: ['request', 'csv_doc_ids', 'no_retrieval', 'only_sources', 'exclude_sources'],
};

function isSettingSupportedForStepType(setting, stepType) {
  if (!stepType) return !setting.supportedStepTypes;
  if (setting.supportedStepTypes) {
    return setting.supportedStepTypes.includes(stepType);
  }
  if (setting.excludedStepTypes) {
    return !setting.excludedStepTypes.includes(stepType);
  }
  return true;
}

const VALID_PLAN_STEP_SETTINGS = [
  new PlanStepSettings({ keyName: 'step_id', label: 'Step ID', inputType: PLAN_STEP_TYPES.text, prioritySort: 1 }),
  new PlanStepSettings({
    keyName: 'step_type',
    label: 'Type',
    inputType: PLAN_STEP_TYPES.dropdown,
    dropdownOptions: STEP_TYPE_OPTIONS,
    prioritySort: 5,
  }),
  new PlanStepSettings({
    keyName: 'read_memory_objects',
    label: 'Read Memory Objects',
    inputType: PLAN_STEP_TYPES.list,
    prioritySort: 42,
  }),
  new PlanStepSettings({
    keyName: 'write_output_to_memory_objects',
    label: 'Write Output to Memory',
    inputType: PLAN_STEP_TYPES.list,
    prioritySort: 43,
  }),
  new PlanStepSettings({
    keyName: 'write_full_output_to_memory_objects',
    label: 'Write Full Output to Memory',
    inputType: PLAN_STEP_TYPES.list,
    prioritySort: 44,
  }),
  new PlanStepSettings({
    keyName: 'query_list',
    label: 'Query List',
    inputType: PLAN_STEP_TYPES.extendedList,
    excludedStepTypes: ['code', 'data_analyst_agent'],
    prioritySort: 9,
  }),
  new PlanStepSettings({
    keyName: 'code',
    label: 'Code',
    inputType: PLAN_STEP_TYPES.textarea,
    supportedStepTypes: ['code'],
    prioritySort: 10,
  }),
  new PlanStepSettings({
    keyName: 'preprocess',
    label: 'Preprocess',
    inputType: PLAN_STEP_TYPES.functionList,
    prioritySort: 15,
  }),
  new PlanStepSettings({
    keyName: 'settings',
    label: 'Settings',
    inputType: PLAN_STEP_TYPES.advSettings,
    prioritySort: 100,
  }),
  new PlanStepSettings({
    keyName: 'request',
    label: 'Request',
    inputType: PLAN_STEP_TYPES.textarea,
    supportedStepTypes: ['data_analyst_agent'],
    prioritySort: 10,
  }),
  new PlanStepSettings({
    keyName: 'csv_doc_ids',
    label: 'CSV Data',
    inputType: PLAN_STEP_TYPES.csvDocSelection,
    supportedStepTypes: ['data_analyst_agent'],
    prioritySort: 11,
  }),
  new PlanStepSettings({
    keyName: 'no_retrieval',
    label: 'Analyze attached CSVs only',
    inputType: PLAN_STEP_TYPES.bool,
    supportedStepTypes: ['data_analyst_agent'],
    prioritySort: 12,
  }),
  new PlanStepSettings({
    keyName: 'only_sources',
    label: 'Only Sources',
    inputType: PLAN_STEP_TYPES.fixedOptionList,
    fixedOptions: DATA_ANALYST_SOURCES,
    supportedStepTypes: ['data_analyst_agent'],
    prioritySort: 13,
  }),
  new PlanStepSettings({
    keyName: 'exclude_sources',
    label: 'Exclude Sources',
    inputType: PLAN_STEP_TYPES.fixedOptionList,
    fixedOptions: DATA_ANALYST_SOURCES,
    supportedStepTypes: ['data_analyst_agent'],
    prioritySort: 14,
  }),
];

const PlanStepParameter = ({
  parentNodeId,
  data,
  nodeData,
  onSettingChange,
  onRemoveSetting,
  actions,
  updateNodeData,
}) => {
  if (data.inputType === PLAN_STEP_TYPES.list) {
    return (
      <StringListParameter
        data={data}
        value={nodeData.planStepData[data.keyName]}
        onChange={onSettingChange}
        onRemove={onRemoveSetting}
      />
    );
  }
  if (data.inputType === PLAN_STEP_TYPES.functionList) {
    return (
      <FunctionListParameter
        data={data}
        value={nodeData.planStepData[data.keyName]}
        onChange={onSettingChange}
        onRemove={onRemoveSetting}
      />
    );
  }
  if (data.inputType === PLAN_STEP_TYPES.extendedList) {
    return (
      <ExtendedListParameter
        data={data}
        value={nodeData.planStepData[data.keyName]}
        onChange={onSettingChange}
        onRemove={onRemoveSetting}
      />
    );
  }
  if (data.inputType === PLAN_STEP_TYPES.mixedList) {
    return (
      <MixedListParameter
        data={data}
        value={nodeData.planStepData[data.keyName]}
        onChange={onSettingChange}
        onRemove={onRemoveSetting}
      />
    );
  }
  if (data.inputType === PLAN_STEP_TYPES.listOrValue) {
    return (
      <StringListOrValueParameter
        data={data}
        value={nodeData.planStepData[data.keyName]}
        onChange={onSettingChange}
        onRemove={onRemoveSetting}
      />
    );
  }
  if (data.inputType === PLAN_STEP_TYPES.docSelection) {
    return (
      <DocSelectionParameter
        parentNodeId={parentNodeId}
        data={data}
        nodeData={nodeData}
        updateNodeData={updateNodeData}
        value={nodeData.planStepData[data.keyName]}
        onChange={onSettingChange}
        onRemove={onRemoveSetting}
        openDocSelectionModal={() => actions.openDocSelectionModal(parentNodeId)}
      />
    );
  }
  if (data.inputType === PLAN_STEP_TYPES.text) {
    return (
      <InputParameter
        data={data}
        value={nodeData.planStepData[data.keyName]}
        onChange={onSettingChange}
        onRemove={onRemoveSetting}
      />
    );
  }
  if (data.inputType === PLAN_STEP_TYPES.textarea) {
    return (
      <TextAreaParameter
        data={data}
        value={nodeData.planStepData[data.keyName]}
        onChange={onSettingChange}
        onRemove={onRemoveSetting}
      />
    );
  }
  if (data.inputType === PLAN_STEP_TYPES.dropdown) {
    return (
      <DropdownParameter
        data={data}
        value={nodeData.planStepData[data.keyName]}
        onChange={onSettingChange}
        onRemove={onRemoveSetting}
      />
    );
  }
  if (data.inputType === PLAN_STEP_TYPES.bool) {
    return (
      <BoolParameter
        data={data}
        value={nodeData.planStepData[data.keyName]}
        onChange={onSettingChange}
        onRemove={onRemoveSetting}
      />
    );
  }
  if (data.inputType === PLAN_STEP_TYPES.advSettings) {
    return (
      <AdvancedSettingsParameter
        parentNodeId={parentNodeId}
        data={data}
        value={nodeData.planStepData[data.keyName]}
        onChange={onSettingChange}
        onRemove={onRemoveSetting}
        openDocSelectionModal={(value, onChange) => actions.openDocSelectionModal(parentNodeId, value, onChange)}
      />
    );
  }
  if (data.inputType === PLAN_STEP_TYPES.csvDocSelection) {
    return (
      <CsvDocSelectionParameter
        data={data}
        value={nodeData.planStepData[data.keyName]}
        onChange={onSettingChange}
        onRemove={onRemoveSetting}
        openCsvDocSelectionModal={(currentDocs, onDocsChange) =>
          actions.openCsvDocSelectionModal(parentNodeId, currentDocs, onDocsChange)
        }
      />
    );
  }
  if (data.inputType === PLAN_STEP_TYPES.fixedOptionList) {
    return (
      <FixedOptionListParameter
        data={data}
        value={nodeData.planStepData[data.keyName]}
        onChange={onSettingChange}
        onRemove={onRemoveSetting}
      />
    );
  }

  return (
    <AgentFlowParameterWrapper>
      <Body5>
        Unsupported plan step input type: {data.keyName}: {'<type>'} {data.inputType}
      </Body5>
    </AgentFlowParameterWrapper>
  );
};

function generateSettingsFromPlanStepData(data) {
  const settings = [];
  const keys = Object.keys(data);
  keys.forEach((key) => {
    const setting = VALID_PLAN_STEP_SETTINGS.find((setting) => setting.keyName === key);
    if (setting && isSettingSupportedForStepType(setting, data.step_type)) {
      settings.push(setting);
    }
  });
  return settings.sort((a, b) => a.prioritySort - b.prioritySort);
}

export default React.memo((props) => {
  const { data, id, isConnectable } = props;

  const [availableParameters, setAvailableParameters] = React.useState([]);
  const [parameters, setParameters] = React.useState(() => generateSettingsFromPlanStepData(data.planStepData || {}));

  const [showAddPlanStepSettingSelector, setShowAddPlanStepSettingSelector] = React.useState(false);
  const [selectedAddSetting, setSelectedAddSetting] = React.useState(null);

  const onRemoveConnectedEdges = (nodeId, handleId, handleType) => {
    data.actions.removeHandleEdges(nodeId, handleId, handleType);
  };

  const currentStepType = data.planStepData?.step_type;

  React.useEffect(() => {
    const filtered = VALID_PLAN_STEP_SETTINGS.filter(
      (setting) =>
        parameters.every((s) => s.keyName !== setting.keyName) &&
        isSettingSupportedForStepType(setting, currentStepType)
    );
    setAvailableParameters(filtered);
  }, [currentStepType, parameters]);

  React.useEffect(() => {
    const unsupportedParameters = parameters.filter(
      (setting) => !isSettingSupportedForStepType(setting, currentStepType)
    );
    if (unsupportedParameters.length === 0) return;

    setParameters((prevState) =>
      prevState.filter((setting) => isSettingSupportedForStepType(setting, currentStepType))
    );

    unsupportedParameters.forEach((setting) => {
      if (setting.keyName in (data.planStepData || {})) {
        data.actions.removeNodePlanStepKeyName(id, setting.keyName);
      }
    });
  }, [currentStepType, data.actions, data.planStepData, id, parameters]);

  React.useEffect(() => {
    if (!data.planStepData) {
      setParameters([]);
      return;
    }

    setParameters((prevState) => {
      const syncedParameters = generateSettingsFromPlanStepData(data.planStepData);
      const pendingParameters = prevState.filter(
        (setting) =>
          !(setting.keyName in data.planStepData) &&
          isSettingSupportedForStepType(setting, data.planStepData?.step_type)
      );

      return [...syncedParameters, ...pendingParameters].sort((a, b) => a.prioritySort - b.prioritySort);
    });
  }, [data.planStepData]);

  const onSettingChange = (keyName, stepData) => {
    if (keyName === 'step_type') {
      const requiredKeys = STEP_TYPE_PARAMETER_LOOKUP[stepData] || [];
      const requiredParameters = VALID_PLAN_STEP_SETTINGS.filter((setting) => requiredKeys.includes(setting.keyName));

      if (requiredParameters.length > 0) {
        setParameters((prevState) => {
          const nextState = [...prevState];

          requiredParameters.forEach((setting) => {
            if (nextState.every((existingSetting) => existingSetting.keyName !== setting.keyName)) {
              nextState.push(setting);
            }
          });

          return nextState.sort((a, b) => a.prioritySort - b.prioritySort);
        });
      }
    }

    return data.actions.updateNodePlanStepData(id, { [keyName]: stepData });
  };
  const onRemoveSetting = (keyName) => {
    setParameters((prevState) => {
      const state = prevState.filter((s) => s.keyName !== keyName);
      return state;
    });
    return data.actions.removeNodePlanStepKeyName(id, keyName);
  };
  const onSettingSelectionChange = (e) => {
    setSelectedAddSetting(e.target.value);
  };
  const onAddPlanStepParameterClick = () => {
    const setting = VALID_PLAN_STEP_SETTINGS.filter((s) => s.keyName === selectedAddSetting)[0];
    if (!setting) return null;

    setParameters((prevState) => {
      const state = [...prevState, setting];
      return state;
    });
    if (setting.defaultValue !== undefined) {
      onSettingChange(setting.keyName, setting.defaultValue);
    }
    setShowAddPlanStepSettingSelector(false);
  };
  const onShowAddPlanStepParameterSelectorClick = () => {
    setShowAddPlanStepSettingSelector(true);
  };

  const onUseConditionalNextStepsClick = () => {
    data.actions.removeSourceEdges(id);
    onSettingChange('conditional_next_step_ids', { Yes: [], No: [] });
  };

  const onClearConditionalNextStepsClick = () => {
    data.actions.removeSourceEdges(id);
    onRemoveSetting('conditional_next_step_ids');
  };

  const isConditionalNextStep = 'conditional_next_step_ids' in data.planStepData;
  const restrictedParameterKeys = STEP_TYPE_PARAMETER_LOOKUP[data.planStepData.step_type] || null;
  const visibleAvailableParameters = restrictedParameterKeys
    ? availableParameters.filter((setting) => restrictedParameterKeys.includes(setting.keyName))
    : availableParameters;

  // Get validation results from data
  const validations = data.validations || [];

  const name = 'Plan Step';
  return (
    <NodeWrapper data-id={id} name={name} data={data} nodeData={props}>
      <NodeHeader
        id={id}
        name={name}
        Left={8}
        actions={data.actions}
        badge={<NodeValidationBadge nodeId={id} validations={validations} />}
      />

      <PlanStepsTargetHandle
        {...props}
        isConnectable={isConnectable}
        nodeId={id}
        onRemoveConnectedEdges={onRemoveConnectedEdges}
      />
      {isConditionalNextStep ? (
        <>
          <PlanStepsYesConditionalSourceHandle
            {...props}
            isConnectable={isConnectable}
            nodeId={id}
            onRemoveConnectedEdges={onRemoveConnectedEdges}
          />
          <PlanStepsNoConditionalSourceHandle
            {...props}
            isConnectable={isConnectable}
            nodeId={id}
            onRemoveConnectedEdges={onRemoveConnectedEdges}
          />
        </>
      ) : (
        <PlanStepsSourceHandle
          {...props}
          isConnectable={isConnectable}
          nodeId={id}
          onRemoveConnectedEdges={onRemoveConnectedEdges}
        />
      )}
      <ConditionalNextStepsButtonWrapper className="read-only-hide">
        {isConditionalNextStep ? (
          <Container top={54}>
            <TransparentFlatButton onClick={onClearConditionalNextStepsClick}>
              Remove Conditional Next Steps
            </TransparentFlatButton>
          </Container>
        ) : (
          <Container top={30}>
            <TransparentFlatButton onClick={onUseConditionalNextStepsClick}>
              Use Conditional Next Steps
            </TransparentFlatButton>
          </Container>
        )}
      </ConditionalNextStepsButtonWrapper>

      <ContentWrapper horizontal={4} top={8}>
        <Container top={4}>
          <SettingStyling>
            {parameters.map((s) => (
              <PlanStepParameter
                key={s.keyName}
                data={s}
                parentNodeId={id}
                nodeData={data}
                updateNodeData={data.actions.updateNodeData}
                onSettingChange={onSettingChange}
                onRemoveSetting={onRemoveSetting}
                actions={data.actions}
              />
            ))}
          </SettingStyling>

          {showAddPlanStepSettingSelector ? (
            <Container top={16} className="read-only-hide">
              <Container>
                <Body5 bold>Select a parameter to add:</Body5>
              </Container>
              <Container row verticallyCenter fullWidth top={8}>
                <SettingStyling>
                  <select value={selectedAddSetting} onChange={onSettingSelectionChange}>
                    <option value={null}>Select One...</option>
                    {visibleAvailableParameters.map((p) => (
                      <option key={p.keyName} value={p.keyName}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </SettingStyling>
                <AddSelectedSettingButton>
                  <FlatButton onClick={onAddPlanStepParameterClick}>Add</FlatButton>
                </AddSelectedSettingButton>
              </Container>
            </Container>
          ) : (
            <Container top={16} className="read-only-hide">
              <SkeletonButton onClick={onShowAddPlanStepParameterSelectorClick}>Add Plan Step Parameter</SkeletonButton>
            </Container>
          )}
          <NodeValidationDisplay nodeId={id} validations={validations} top={24} />

          <StepJsonDisplay data={data} />
        </Container>
      </ContentWrapper>

      <NodeDebugger id={id} />
    </NodeWrapper>
  );
});
